/**
 * Theme preference — Tide (light) vs Abyss (dark), and "match my phone".
 *
 * D-2: ONE form language, TWO palettes. The palettes are defined in `palette.ts`
 * and carried by `index.css` as two blocks keyed off `:root[data-theme]`. This
 * module is only the *switch*: it decides which palette is in force, stamps it,
 * and records the user's choice.
 *
 * It always stamps the **resolved** theme — "system" writes whatever the OS says
 * and follows it as it changes — so the CSS never needs a media-query copy of a
 * palette to fall back on.
 *
 * The preference is a per-device UI setting, so it lives in `localStorage` — not
 * in the synced `Profile`. Nothing here crosses the wire, so there is no schema,
 * migration, or tenant-isolation surface to get wrong.
 *
 * No-flash boot is handled by a tiny inline script in `index.html` that reads the
 * same key, resolves it the same way, and stamps `data-theme` before first paint. `startTheme()` re-applies
 * on mount to keep the status-bar tint and the OS-change listener in sync.
 */

import { bridge } from "@/native/impl";
import { GROUND } from "@/lib/palette";

export type ThemePreference = "system" | "light" | "dark";

const STORAGE_KEY = "aegae.theme";

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** The stored choice, defaulting to "system" when unset or unreadable. */
export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private-mode / disabled storage: fall back to following the OS. This is a
    // cosmetic preference, so there is nothing to report and nothing at stake.
  }
  return "system";
}

/** The palette actually in force right now, resolving "system" against the OS. */
export function effectiveTheme(pref: ThemePreference = getThemePreference()): "light" | "dark" {
  return pref === "system" ? (prefersDark() ? "dark" : "light") : pref;
}

function apply(pref: ThemePreference): void {
  // Always the resolved theme, "system" included: index.css has no media-query
  // copy of a palette, so an unstamped root would be Tide on a dark phone.
  const theme = effectiveTheme(pref);
  document.documentElement.setAttribute("data-theme", theme);
  void bridge.setTheme(theme).catch((cause: unknown) => {
    console.error("Could not update the system bar theme:", cause);
  });

  // Keep the browser/status-bar tint in step with what is actually on screen.
  // The media-scoped metas can't express an explicit override, so drive both.
  const color = GROUND[theme];
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute("content", color);
  }
}

/** Record and apply a choice. The water follows via its `data-theme` observer. */
export function setThemePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // Storage unavailable — apply for this session anyway; the choice just
    // won't survive a reload. Nothing to surface for a cosmetic setting.
  }
  apply(pref);
}

let started = false;

/**
 * Apply the stored preference on boot and, while in "system" mode, follow live
 * OS light/dark changes for the status-bar tint. Idempotent.
 */
export function startTheme(): void {
  if (started) return;
  started = true;
  apply(getThemePreference());
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      if (getThemePreference() === "system") apply("system");
    });
  }
}
